/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FormData,
  SectionKey,
  getWorkflowSectionKeys,
} from '../../../../types';
import { createInitialFormState } from '../../../container/forms/hooks/useFormState';
import {
  FormState,
  SectionProgress,
} from '../../../container/forms/types/core/form-state.types';
import {
  PaymentWorkflowType,
  getWorkflowTypeFromDisplayName,
} from '../../../container/forms/types/api/graph.types';
import { createLogger } from '../../../../utils/logger';
import { WORKFLOW_SECTION_MAP } from './constants';
import { getOvertimeSectionFlow } from '../../../container/forms/schemas/OvertimeApproval/utils';
import {
  getGiftHospitalitySectionFlow,
  isGiftHospitalityWorkflow,
} from '../../../container/forms/schemas/GiftHospitalityDonation/authoriserRules';

const logger = createLogger('WorkflowMapper');

const MULTI_SECTION_WORKFLOW_TYPES = new Set<PaymentWorkflowType>([
  PaymentWorkflowType.DubaiPaymentApproval,
  PaymentWorkflowType.DubaiPaymentFromOfficeAcc,
  PaymentWorkflowType.DubaiTransferRequisition,
  PaymentWorkflowType.OvertimeApproval,
  PaymentWorkflowType.GiftHospitalityDonationUk,
  PaymentWorkflowType.GiftHospitalityDonationDubai,
  PaymentWorkflowType.GiftHospitalityDonationDublin,
  // PLOP_INJECT_MULTI_SECTION_WORKFLOW
]);

export const isMultiSectionWorkflow = (
  workflowType: PaymentWorkflowType,
): boolean => {
  return MULTI_SECTION_WORKFLOW_TYPES.has(workflowType);
};

/** Determine workflow type from FormData (workflowType, name, or section keys). */
export const determineWorkflowType = (
  workflowData: FormData,
): PaymentWorkflowType => {
  if (workflowData.workflowType) {
    return workflowData.workflowType;
  }
  if (workflowData.name) {
    const workflowType = getWorkflowTypeFromDisplayName(workflowData.name);
    if (workflowType !== PaymentWorkflowType.Undefined) {
      return workflowType;
    }
  }
  const sectionKeys = workflowData.sections.map((s) => s.sectionKey);
  if (sectionKeys.includes('AddPaymentfromOfficeAccForm' as SectionKey)) {
    return PaymentWorkflowType.DubaiPaymentFromOfficeAcc;
  }
  if (sectionKeys.includes('PaymentfromOfficeAccForm' as SectionKey)) {
    return PaymentWorkflowType.DubaiPaymentFromOfficeAcc;
  }
  if (
    sectionKeys.some((key) =>
      [
        'overtime_approval_request_details',
        'overtime_approval_line_manager',
        'overtime_approval_finance_confirmation',
      ].includes(key),
    )
  ) {
    return PaymentWorkflowType.OvertimeApproval;
  }
  if (
    sectionKeys.some((key) =>
      [
        'gift_hospitality_donation_uk_request_details',
        'gift_hospitality_donation_uk_approval',
      ].includes(key),
    )
  ) {
    return PaymentWorkflowType.GiftHospitalityDonationUk;
  }
  if (
    sectionKeys.some((key) =>
      [
        'gift_hospitality_donation_dubai_request_details',
        'gift_hospitality_donation_dubai_approval',
      ].includes(key),
    )
  ) {
    return PaymentWorkflowType.GiftHospitalityDonationDubai;
  }
  if (
    sectionKeys.some((key) =>
      [
        'gift_hospitality_donation_dublin_request_details',
        'gift_hospitality_donation_dublin_approval',
      ].includes(key),
    )
  ) {
    return PaymentWorkflowType.GiftHospitalityDonationDublin;
  }
  // PLOP_INJECT_DETERMINE_WORKFLOW_TYPE
  if (
    sectionKeys.some((key) =>
      ['payment_details', 'resp_lawyer', 'first_partner'].includes(key),
    )
  ) {
    return PaymentWorkflowType.DubaiPaymentApproval;
  }
  return PaymentWorkflowType.Undefined;
};

/** Map API section key to internal SectionKey for a given workflow type. */
export const mapSectionKey = (
  originalSectionKey: string,
  workflowType: PaymentWorkflowType,
): SectionKey => {
  const sectionMap = WORKFLOW_SECTION_MAP[workflowType];
  return sectionMap[originalSectionKey] || '';
};

/**
 * Determine which section is "current" for the workflow (for initial form view).
 * Uses status → section mapping, or first incomplete section, or last section if all complete.
 */
export const determineCurrentSection = (
  workflowData: FormData,
  workflowType: PaymentWorkflowType,
): SectionKey => {
  let sectionFlow = getWorkflowSectionKeys(workflowType);

  if (workflowType === PaymentWorkflowType.OvertimeApproval) {
    const requestDetailsSection = workflowData.sections?.find(
      (section) => section.sectionKey === 'overtime_approval_request_details',
    );
    sectionFlow = getOvertimeSectionFlow(
      requestDetailsSection?.gridData ?? workflowData,
    ) as SectionKey[];
  }

  if (isGiftHospitalityWorkflow(workflowType)) {
    sectionFlow = getGiftHospitalitySectionFlow(
      sectionFlow,
      workflowData
    ) as SectionKey[];
  }

  // Include payment_hold_review when the workflow has that section (on hold)
  const hasPaymentHoldReview = workflowData.sections?.some(
    (s) => s.sectionKey === 'payment_hold_review',
  );
  if (
    hasPaymentHoldReview &&
    workflowType === PaymentWorkflowType.DubaiPaymentApproval &&
    !sectionFlow.includes('payment_hold_review')
  ) {
    const paymentConfirmationIndex = sectionFlow.indexOf(
      'payment_confirmation',
    );
    sectionFlow = [...sectionFlow];
    if (paymentConfirmationIndex !== -1) {
      sectionFlow.splice(paymentConfirmationIndex, 0, 'payment_hold_review');
    } else {
      sectionFlow.push('payment_hold_review');
    }
  }

  const mappedStatus = mapSectionKey(workflowData.status, workflowType);

  if (mappedStatus === 'rejected_form') {
    const apiCurrentSection = (workflowData as any).currentSection;
    if (apiCurrentSection) {
      logger.debug('Status is Rejected, using API currentSection', {
        status: workflowData.status,
        apiCurrentSection,
      });
      return apiCurrentSection as SectionKey;
    }
    const firstIncomplete = workflowData.sections.find((s) => !s.isComplete);
    if (firstIncomplete) {
      logger.debug('Status is Rejected, using first incomplete section', {
        status: workflowData.status,
        firstIncompleteSection: firstIncomplete.sectionKey,
      });
      return firstIncomplete.sectionKey as SectionKey;
    }
    if (workflowData.sections.length > 0) {
      const first = workflowData.sections[0].sectionKey as SectionKey;
      logger.debug('Status is Rejected, using first section as fallback', {
        status: workflowData.status,
        firstSection: first,
      });
      return first;
    }
  }

  if (mappedStatus) {
    if (sectionFlow.includes(mappedStatus)) {
      logger.debug('Status mapped to section', {
        status: workflowData.status,
        mappedSection: mappedStatus,
      });
      return mappedStatus;
    }
  }

  logger.debug(
    'Status did not map to section key, determining from completion status',
    { status: workflowData.status },
  );

  const sections = workflowData.sections || [];
  for (const expectedSectionKey of sectionFlow) {
    const section = sections.find((s) => s.sectionKey === expectedSectionKey);
    if (section && !section.isComplete) {
      logger.debug('Found first incomplete section', {
        expectedSectionKey,
        originalSectionKey: section.sectionKey,
      });
      return expectedSectionKey;
    }
  }

  if (sectionFlow.length > 0) {
    const lastSection = sectionFlow[sectionFlow.length - 1];
    logger.debug('All sections complete, returning last section', {
      lastSection,
    });
    return lastSection;
  }

  if (sections.length > 0) {
    const fallback = sections[0].sectionKey as SectionKey;
    logger.debug('Using fallback section', { fallbackSection: fallback });
    return fallback;
  }

  const ultimateFallback = sectionFlow[0] || 'payment_details';
  logger.debug('Using ultimate fallback', { ultimateFallback });
  return ultimateFallback;
};

/** Build initial form state for a workflow type (multi- vs single-section). */
export const createInitialFormStateForWorkflow = (
  workflowType: PaymentWorkflowType,
): Partial<FormState> => {
  const baseFormState = createInitialFormState(workflowType);
  if (isMultiSectionWorkflow(workflowType)) {
    return baseFormState;
  }
  return {
    ...baseFormState,
    sectionValidation: {} as Record<SectionKey, boolean>,
    sectionProgress: {} as SectionProgress,
    completedSections: [],
  };
};
